import { api } from "./api";

/**
 * Everything the Patterns page does that is not JSX.
 *
 * This page is the audit trail for an automatic decision: the learner hides
 * listings from the browse feed without being asked, and this is the only place
 * that hiding is visible or reversible. So the formatting here leans toward
 * being explicit about what a pattern is doing rather than terse.
 */

export interface BlockedListing {
  key: string;
  title: string;
  url: string;
  price: number | null;
  matched_pattern: string;
  filtered_at: string;
}

export interface Keyword {
  keyword: string;
  blocked_count: number;
  overridden: boolean;
}

export interface ArtistPattern {
  artist: string;
  keywords: Keyword[];
  source_count: number;
  example_titles: string[];
  blocked: BlockedListing[];
  blocked_count: number;
  dormant: boolean;
}

export interface PatternsPage {
  artists: ArtistPattern[];
  total_patterns: number;
  total_blocked: number;
  artists_with_blocked: number;
}

export async function fetchPatterns(): Promise<PatternsPage> {
  const res = await api.authedFetch("/api/patterns");
  if (!res.ok) throw new Error(`Could not load patterns (HTTP ${res.status})`);
  return (await res.json()) as PatternsPage;
}

/**
 * Mark a keyword as wrong.
 *
 * Returns the whole rebuilt page rather than void, because an override
 * re-derives the artist and the keyword usually LEAVES the list rather than
 * staying with a flag. Guessing at that client-side would show a keyword the
 * server has already dropped.
 *
 * A 404 means this view and the server disagree about which artists have
 * patterns, which happens when a re-derive removed one between load and click.
 */
export async function overrideKeyword(artist: string, keyword: string): Promise<PatternsPage> {
  const res = await api.authedFetch("/api/patterns/overrides", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ artist, keyword }),
  });
  if (res.status === 404) {
    throw new Error("That pattern no longer exists. Reload to see the current list.");
  }
  if (!res.ok) throw new Error(`Could not override the keyword (HTTP ${res.status})`);
  return (await res.json()) as PatternsPage;
}

/**
 * The page-level summary.
 *
 * It spells out what "0 blocked" means, because the natural reading is "this
 * pattern does nothing" and the truth is the opposite: it is armed and waiting
 * for the next matching listing the scraper finds.
 */
export function summaryLine(page: PatternsPage): string {
  const p = `${page.total_patterns} active pattern${page.total_patterns === 1 ? "" : "s"}`;
  const b = `${page.total_blocked} listing${page.total_blocked === 1 ? "" : "s"} currently blocked`;
  const a = `${page.artists_with_blocked} artist${page.artists_with_blocked === 1 ? "" : "s"}`;
  return `${p} · ${b} across ${a}. A pattern blocking 0 is dormant, not dead: nothing in the current catalog matches, but it will catch future listings.`;
}

/** The per-artist line: what it is doing now, and how much evidence taught it. */
export function artistLine(artist: ArtistPattern): string {
  const learned = `learned from ${artist.source_count} dismissal${artist.source_count === 1 ? "" : "s"}`;
  if (artist.dormant) return `Dormant · ${learned}`;
  const n = artist.blocked_count;
  return `${n} listing${n === 1 ? "" : "s"} blocked now · ${learned}`;
}

/** `wisdom · 12`, or just `ghost` when it is catching nothing right now. */
export function keywordLabel(keyword: Keyword): string {
  return keyword.blocked_count > 0 ? `${keyword.keyword} · ${keyword.blocked_count}` : keyword.keyword;
}

/** How many blocked listings the server did not send, for the "+N more" note. */
export function hiddenBlockedCount(artist: ArtistPattern): number {
  return Math.max(0, artist.blocked_count - artist.blocked.length);
}
